import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import PromptGrid from "@/components/prompts/PromptGrid";
import { getPromptsByCategory, getCategories } from '@/services/promptService';
import type { Prompt, Category } from '@/services/promptService';
import { toast } from 'sonner';

const CategoryPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categoryName, setCategoryName] = useState('');
  const [validCategory, setValidCategory] = useState(true);
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    // Load all categories once
    const loadCategories = async () => {
      try {
        const allCategories = await getCategories();
        setCategories(allCategories);
        return allCategories;
      } catch (error) {
        console.error('Error loading categories:', error);
        return [];
      }
    };

    const loadPrompts = async () => {
      if (!slug) return;
      
      setIsLoading(true);
      
      try {
        // First check if the category exists
        const allCategories = await loadCategories();
        const category = allCategories.find(cat => cat.slug === slug);
        
        if (!category) {
          console.log(`Category with slug "${slug}" not found`);
          setValidCategory(false);
          const formattedName = slug
            ? slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
            : 'Unknown';
          setCategoryName(formattedName);
          setIsLoading(false);
          
          // If we have categories but this one doesn't exist, redirect to a valid one
          if (allCategories.length > 0) {
            toast.error(`Category "${formattedName}" doesn't exist.`);
            navigate(`/category/${allCategories[0].slug}`);
          }
          return;
        }
        
        // If category exists, fetch prompts
        setCategoryName(category.name);
        const results = await getPromptsByCategory(slug);
        console.log(`Loaded ${results.length} prompts for category: ${category.name}`);
        setPrompts(results);
        setValidCategory(true);
      } catch (error) {
        console.error('Error loading category prompts:', error);
        toast.error(`Failed to load prompts for category: ${slug}`);
        setValidCategory(false);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadPrompts();
  }, [slug, navigate]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <header className="mb-10">
            <h1 className="text-3xl font-bold">{categoryName} Prompts</h1>
            <p className="mt-2 text-muted-foreground">
              Browse our collection of {categoryName.toLowerCase()} prompts
            </p>
          </header>
          
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : !validCategory ? (
            <div className="text-center py-20">
              <h3 className="text-xl font-medium mb-2">Category not found</h3>
              <p className="text-muted-foreground">
                The category "{categoryName}" doesn't exist. 
                Please check the URL or browse the available categories.
              </p>
              {categories.length > 0 && (
                <div className="mt-8">
                  <h4 className="font-medium mb-4">Available categories:</h4>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {categories.map(cat => (
                      <a 
                        key={cat.id} 
                        href={`/category/${cat.slug}`}
                        className="px-4 py-2 bg-secondary/50 rounded-md hover:bg-secondary transition-colors"
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : prompts.length > 0 ? (
            <PromptGrid prompts={prompts} columns={3} />
          ) : (
            <div className="text-center py-20">
              <h3 className="text-xl font-medium mb-2">No prompts found</h3>
              <p className="text-muted-foreground">
                We don't have any prompts in this category yet.
              </p>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default CategoryPage;
